import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Shield, Award, Target, Eye, Cpu, Globe, Users, Zap,
  ArrowRight, CheckCircle, Quote, TrendingUp, Clock, MapPin, Star
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEO';
import { SEO_KEYWORDS } from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About HumanOnRent.AI - Reconnecting India, One Human at a Time | Set India Business Pvt Ltd"
        description="HumanOnRent.AI was born from a simple truth: in a hyper-connected digital world, real human connection is disappearing. We're India's first AI-driven platform bridging this gap with verified humans for every occasion."
        keywords={SEO_KEYWORDS.about}
        url="https://humanonrent.ai/about"
      />

      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-50/80 via-white to-white" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-medium mb-8">
              <Heart className="w-4 h-4 text-red-400" />
              Reconnecting India, One Human at a Time
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              We Don't Rent Humans.<br />
              We <span className="gradient-text">Restore Connections.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="relative">
            <Quote className="w-16 h-16 text-orange-500/30 absolute -top-4 -left-4" />
            <blockquote className="text-xl sm:text-2xl leading-relaxed font-light pl-8 border-l-4 border-orange-500">
              <p className="mb-6">
                "People are extremely active on social media, but disconnected from relatives, 
                neighbours, and even close friends. When they need help -- medical assistance, 
                someone to shop with, or even just a movie partner -- they often have 
                <span className="text-orange-400 font-semibold"> no one to turn to.</span>"
              </p>
            </blockquote>
            <p className="text-gray-400 mt-8 text-lg leading-relaxed">
              This isn't just a quote. This is the reality of modern India. A nation of 1.4 billion 
              people where loneliness is becoming an epidemic. Where a senior citizen needs someone 
              to accompany them to a hospital but has no one available. Where a young professional 
              moves to a new city and doesn't know a single person. Where a single parent needs 
              help but doesn't want to burden anyone.
            </p>
            <p className="text-gray-400 mt-4 text-lg leading-relaxed">
              <span className="text-white font-medium">That's why HumanOnRent.AI exists.</span> Not as a luxury. 
              As a <span className="text-orange-400 font-medium">necessity</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 font-semibold text-sm tracking-wide uppercase">Our Story</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-6">
                Born from a Truth That Nobody Was Addressing
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  India's digital revolution connected us to the world but disconnected us from 
                  each other. We have 800 million internet users, yet millions spend their weekends 
                  alone. Elderly parents wait for visits that never come. Patients go to hospitals 
                  unaccompanied. New city residents eat dinner alone for months.
                </p>
                <p>
                  We saw this gap widening every day. The temples were crowded but the hearts were 
                  empty. The malls were full but the connections were hollow. Social media had 
                  billions of posts but zero real presence when someone truly needed a human being 
                  standing next to them.
                </p>
                <p className="text-gray-900 font-medium">
                  So we built HumanOnRent.AI -- India's first platform where you can find a verified, 
                  trusted human for any moment that matters. Not a chatbot. Not a virtual friend. 
                  A real, living, breathing human being who shows up.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { icon: Heart, title: "The Lonely Grandmother", desc: "Who needed someone to take her to the doctor. Her grandchildren lived 2,000 km away. She found a caring provider on our platform within minutes." },
                { icon: MapPin, title: "The New City Transfer", desc: "A software engineer transferred to Bangalore, knowing nobody. Used HumanOnRent.AI to find a city buddy for weekends. Now has a life beyond code." },
                { icon: Users, title: "The Wedding Without Friends", desc: "A woman attending a family wedding alone in another state. Hired a provider as her event partner. She didn't have to explain why she came alone." }
              ].map((story, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <story.icon className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{story.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{story.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                To ensure no Indian ever faces a moment alone when they need someone by their side. 
                Whether it's a hospital visit, a celebration, or just an evening walk -- we're making 
                real human connection accessible, affordable, and available across every pin code in India.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                To build a world where loneliness is a choice, not a circumstance. Where every Indian -- 
                from metro cities to small towns -- has instant access to verified, trustworthy humans 
                who can help, support, and be present. We envision empowering millions to earn with dignity 
                while serving their communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Real Problem */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm tracking-wide uppercase">The Disconnect</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3">
              India's Silent Crisis
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                stat: "82%",
                title: "Feel Disconnected",
                desc: "Despite 700+ WhatsApp groups, over 82% of urban Indians say they have nobody to call for real-world help in an emergency.",
                color: "from-red-500 to-orange-500"
              },
              { 
                stat: "67%",
                title: "Elderly Live Alone",
                desc: "67% of India's senior citizens in urban areas live without daily human interaction beyond basic necessities. They need presence, not prescriptions.",
                color: "from-orange-500 to-amber-500"
              },
              { 
                stat: "45M+",
                title: "Solo Migrants",
                desc: "Over 45 million Indians move to new cities for work every year. Most spend their first 6 months without a single friend to call their own.",
                color: "from-amber-500 to-yellow-500"
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className={`text-5xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-3`}>
                  {item.stat}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold">Not Just a Service. A Lifeline.</h2>
            <p className="text-gray-400 mt-3 text-lg max-w-2xl mx-auto">
              Every feature we build answers one question: "Would this help someone who has no one to turn to?"
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "100% Verified", desc: "Every human on our platform is identity-verified. Your safety is non-negotiable." },
              { icon: Cpu, title: "AI Matching", desc: "Our algorithms find the perfect match based on location, need, ratings, and availability." },
              { icon: Globe, title: "All India Coverage", desc: "Every pin code. Every city. Tier 1, Tier 2, Tier 3. Nobody is too far." },
              { icon: Star, title: "Rated & Reviewed", desc: "Transparent ratings ensure you always get the best. No surprises." },
              { icon: Clock, title: "Instant Booking", desc: "Need someone in 2 hours? Our AI finds available providers near you instantly." },
              { icon: TrendingUp, title: "Earn With Dignity", desc: "Providers earn up to Rs.1 Lac/month doing what they love -- helping people." },
              { icon: Award, title: "Dual Confirmation", desc: "Both hirer and provider must confirm service completion. Fair for everyone." },
              { icon: Zap, title: "20+ Services", desc: "Medical help, travel, shopping, events, elder care, tutoring, and more." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors">
                <item.icon className="w-8 h-8 text-orange-400 mb-3" />
                <h4 className="font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Providers */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-12 border border-orange-100">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-orange-600 font-semibold text-sm tracking-wide uppercase">For Providers</span>
                <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4">
                  Turn Your Time Into Income. Turn Strangers Into Stories.
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  You don't need a degree, a shop, or capital. All you need is yourself, 
                  your time, and the willingness to help someone who needs it. Whether you're 
                  a college student, a homemaker, a retiree, or anyone in between -- your 
                  presence has value. We just help you get paid for it.
                </p>
                <div className="space-y-3">
                  {[
                    "Set your own hours. Work when you want.",
                    "Set your own rate. Earn what you deserve.",
                    "Accept or reject any booking. You're always in control.",
                    "80% of every booking fee goes directly to you.",
                    "Build your reputation through reviews and ratings.",
                    "Flexible membership plans starting at just Rs.249."
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h4 className="font-bold text-gray-900 text-lg mb-4">Earning Potential</h4>
                <div className="space-y-3">
                  {[
                    { hrs: "2 hrs/day", earn: "Rs.15,000-20,000/month" },
                    { hrs: "4 hrs/day", earn: "Rs.35,000-50,000/month" },
                    { hrs: "8 hrs/day", earn: "Rs.70,000-1,00,000/month" }
                  ].map((e, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl">
                      <span className="text-gray-600 text-sm">{e.hrs}</span>
                      <span className="font-bold text-orange-600">{e.earn}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center">Based on Rs.1000/hr base rate. Actual earnings vary.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Trust Is Not a Feature. It's Our Foundation.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Verified Identities", desc: "Every provider goes through identity verification before being listed. No exceptions." },
              { title: "SOS Emergency Button", desc: "One-tap emergency alert system available during every booking. Your safety comes first." },
              { title: "Dual Completion System", desc: "Both parties must confirm service completion before payment is released. Fair and transparent." },
              { title: "Review System", desc: "After every service, hirers rate and review providers. This keeps quality consistently high." },
              { title: "GST-Compliant Invoicing", desc: "Every transaction generates a proper GST tax invoice. Fully legal, fully transparent." },
              { title: "24/7 Support", desc: "Our team is available around the clock. Email, phone, or WhatsApp -- we're here." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djJIMjR2LTJoMTJ6bTAtNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Don't Let Anyone Face a Moment Alone
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Whether you need someone or want to be someone's support -- 
                HumanOnRent.AI is where real connections begin.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth" className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all flex items-center justify-center gap-2" data-testid="about-cta-hire">
                  Get Started <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/auth" className="border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all" data-testid="about-cta-provider">
                  Become a Provider
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-12 border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm">HumanOnRent.AI is a unit of <strong>Set India Business Pvt Ltd</strong></p>
          <p className="text-gray-400 text-xs mt-2">Registered under the Companies Act, 2013 | All rights reserved</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
